"use client";
import { Clock } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addData, baseUrl } from "../../config/data";

export default function QuestionsPage({ setCurrentScreen, onPretestCompleted, pretestToken }) {
    const { lang } = useSelector((state) => state.globalReducer);
    const navigate = useNavigate();

    const [sectionData, setSectionData] = useState(null);
    const [assessmentData, setAssessmentData] = useState(null);
    const [questionData, setQuestionData] = useState(null);
    const effectRun = useRef(false);

    const addResponses = async () => {
        const payload = {
            "sessionID": assessmentData?.sessionID,
            "questionID": questionData[current]?.questionID,
            "selectedAnswerID": answers[current],
            "pretestToken": pretestToken
        }
        addData({
            url: `TestResponses`,
            reqBody: payload
        }).then(async res => {
            if (res?.data) {
                if (current < questionData?.length - 1) {
                    setCurrent(current + 1);
                } else {
                    const sectionNumber = sessionStorage.getItem("sectionNumber");
                    const number = Number(sectionNumber);
                    const assessmentLocal = sessionStorage.getItem("assessmentLocal");
                    const parsedData = JSON.parse(assessmentLocal);
                    if ((number + 1) === parsedData.sections.length) {
                        const sessionID = parsedData?.sessionID ?? parsedData?.SessionID;
                        if (sessionID) {
                            sessionStorage.setItem(`pretestTerminated-${sessionID}`, "true");
                            await fetch(
                                `${baseUrl}/TestSessions/${sessionID}/Terminate?token=${encodeURIComponent(pretestToken)}`,
                                { method: "POST" }
                            ).catch(() => { });
                        }
                        sessionStorage.removeItem("sectionNumber");
                        sessionStorage.removeItem("assessmentLocal");
                        setCurrentScreen(3)
                    } else {
                        setCurrentScreen(1)
                    }
                }
            } else if (typeof res === "string") {
                const message = res.toLowerCase();
                if (
                    message.includes("completed") ||
                    message.includes("already exists") ||
                    message.includes("only one test session")
                ) {
                    sessionStorage.removeItem("sectionNumber");
                    sessionStorage.removeItem("assessmentLocal");
                    onPretestCompleted?.();
                    return;
                }

                if (
                    message.includes("expired") ||
                    message.includes("hard filter") ||
                    message.includes("progressed")
                ) {
                    sessionStorage.removeItem("sectionNumber");
                    sessionStorage.removeItem("assessmentLocal");
                    navigate(`/${lang}/my-application`);
                }
            }
        })
    };


    useEffect(() => {
        if (effectRun.current) return;
        effectRun.current = true;
        const sectionNumber = sessionStorage.getItem("sectionNumber");
        const number = Number(sectionNumber);
        const assessmentLocal = sessionStorage.getItem("assessmentLocal");
        if (assessmentLocal) {
            const parsedData = JSON.parse(assessmentLocal);
            if (parsedData?.pretestToken !== pretestToken) {
                sessionStorage.removeItem("sectionNumber");
                sessionStorage.removeItem("assessmentLocal");
                navigate(`/${lang}/my-application`);
                return;
            }
            const sectionData = parsedData.sections[number];
            setSectionData(sectionData);
            setAssessmentData(parsedData)
            setQuestionData(sectionData?.questions)
            setTimer((sectionData?.duration ?? 0) * 60)
        }
    }, [])

    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [timer, setTimer] = useState(null);

    useEffect(() => {
        if (questionData) {
            const interval = setInterval(() => {
                setTimer((t) => (t > 0 ? t - 1 : 0));
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [questionData]);

    useEffect(() => {
        if (timer === 0) {
            const sectionNumber = sessionStorage.getItem("sectionNumber");
            const number = Number(sectionNumber);
            const assessmentLocal = sessionStorage.getItem("assessmentLocal");
            const parsedData = JSON.parse(assessmentLocal);
            if ((number + 1) === parsedData.sections.length) {
                const sessionID = parsedData?.sessionID ?? parsedData?.SessionID;
                if (sessionID) {
                    sessionStorage.setItem(`pretestTerminated-${sessionID}`, "true");
                    fetch(
                        `${baseUrl}/TestSessions/${sessionID}/Terminate?token=${encodeURIComponent(pretestToken)}`,
                        { method: "POST" }
                    ).catch(() => { });
                }
                sessionStorage.removeItem("sectionNumber");
                sessionStorage.removeItem("assessmentLocal");
                setCurrentScreen(3)
            } else {
                setCurrentScreen(1)
            }
        }
    }, [timer]);

    useEffect(() => {
        if (questionData) {
            setAnswers(Array(questionData.length).fill(null));
        }
    }, [questionData]);

    const minutes = String(Math.floor(timer / 60)).padStart(2, "0");
    const seconds = String(timer % 60).padStart(2, "0");

    const handleNext = () => {
        addResponses();
    };
    const handleSelect = (answerID) => {
        const newAnswers = [...answers];
        newAnswers[current] = answerID;
        setAnswers(newAnswers);
    };
    const progress = ((current + 1) / questionData?.length) * 100;

    return (
        <div className="min-h-screen bg-white flex flex-col pb-32 items-center">
            <div className="w-full max-w-7xl flex flex-row border-b py-4 px-8 justify-between items-center">
                <h1 className="text-[18px] md:text-[20px] font-semibold mr-5">{sectionData?.categoryName}</h1>

                <div className="flex items-center gap-2 bg-[#F4F6F9] px-4 py-2 rounded-lg text-gray-600">
                    <Clock className="h-[18px]" />
                    <span className="font-medium">{minutes}:{seconds}</span>
                </div>
            </div>

            {questionData && <div className="max-w-7xl w-full mx-auto px-8 pt-8">
                <div className="flex justify-center text-sm text-gray-500 mb-1">
                    <span>Soal {current + 1} dari {questionData?.length}</span>
                </div>

                <div className="w-full h-[6px] bg-gray-200 rounded-full mb-8">
                    <div
                        className="h-full bg-red-500 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <h2 className="text-[14px] md:text-[20px] font-medium text-start leading-relaxed mb-5 max-w-7xl md:px-8 px-3">
                    {questionData[current]?.questionText}
                </h2>

                <div className="flex flex-col gap-6 mx-auto w-full max-w-7xl md:px-12 px-3.5">
                    {questionData[current]?.answers?.map((opt, i) => (
                        <label key={i} className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name={`q-${current}`}
                                checked={answers[current] === opt.answerID}
                                onChange={() => handleSelect(opt.answerID)}
                                className="mt-1 h-5 w-5 min-h-5 min-w-5 shrink-0 text-red-500 focus:ring-red-500"
                            />
                            <span className="text-[14px] md:text-[20px] text-gray-700">
                                {opt.answerText}
                            </span>
                        </label>
                    ))}
                </div>
                <button
                    className="group flex flex-row right-0 bg-[#EA2427] text-white border border-[#EA2427]
                        hover:bg-white hover:text-[#EA2427]
                        rounded-full py-2 px-3 md:mx-8 mx-3 mt-10 transition-[1s] font-bold md:mr-2"
                    onClick={handleNext}
                >
                    <span>{current === questionData?.length - 1
                        ? "Kirim Jawaban"
                        : "Selanjutnya"}</span>

                    <img
                        src="/asset/icon/arrow_right_white.svg"
                        className="ml-3 w-[20px] group-hover:hidden"
                    />

                    <img
                        src="/asset/icon/arrow_right_red.svg"
                        className="ml-3 w-[20px] hidden group-hover:block"
                    />
                </button>
            </div>}
        </div>
    );
}
